import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { useIsAdmin } from "../sales/useIsAdmin";
import type { Sale } from "../types";
import { type DashboardPeriod, useDashboardScope } from "./useDashboardScope";

/** Select value standing for the team-wide scope. */
const TEAM_VALUE = "team";

const PERIOD_KEYS: Record<DashboardPeriod, string> = {
  week: "crm.dashboard.scope.period_week",
  month: "crm.dashboard.scope.period_month",
  quarter: "crm.dashboard.scope.period_quarter",
};

/** Quiet control strip, not a card: borderless triggers on the page background. */
const triggerClassName =
  "h-8 w-auto gap-1 border-none bg-transparent px-2 shadow-none text-muted-foreground hover:text-foreground";

/**
 * The dashboard control row: which account manager is being looked at, and over
 * which period. The owner selector is admin-only, since `useDashboardScope`
 * refuses to widen a non-admin's scope anyway.
 */
export const DashboardScopeBar = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { isAdmin } = useIsAdmin();
  const { salesId, isTeam, period, setSalesId, setPeriod, isPending } =
    useDashboardScope();

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: isAdmin },
  );

  if (isPending) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-32" />
      </div>
    );
  }

  const handleOwnerChange = (value: string) => {
    if (value === TEAM_VALUE) {
      setSalesId(null);
      return;
    }
    // Pass the record's own identifier rather than the string the select
    // carries, so the filter keeps the numeric type the data provider expects.
    const sale = sales?.find((candidate) => String(candidate.id) === value);
    setSalesId(sale?.id ?? identity?.id ?? null);
  };

  const otherSales =
    sales?.filter((sale) => String(sale.id) !== String(identity?.id)) ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isAdmin && identity?.id != null ? (
        <Select
          value={isTeam ? TEAM_VALUE : String(salesId)}
          onValueChange={handleOwnerChange}
        >
          <SelectTrigger
            className={triggerClassName}
            aria-label={translate("crm.dashboard.scope.owner")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={String(identity.id)}>
              {translate("crm.dashboard.scope.mine")}
            </SelectItem>
            <SelectItem value={TEAM_VALUE}>
              {translate("crm.dashboard.scope.team")}
            </SelectItem>
            {otherSales.map((sale) => (
              <SelectItem key={sale.id} value={String(sale.id)}>
                {`${sale.first_name} ${sale.last_name}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <Select
        value={period}
        onValueChange={(value) => setPeriod(value as DashboardPeriod)}
      >
        <SelectTrigger
          className={triggerClassName}
          aria-label={translate("crm.dashboard.scope.period")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(PERIOD_KEYS).map(([value, key]) => (
            <SelectItem key={value} value={value}>
              {translate(key)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
